import { memo, ReactNode } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import styled, { css } from 'styled-components';
import { darken } from 'polished';
import {
    IoReorderThreeOutline,
    IoChevronUpOutline,
    IoChevronDownOutline
} from 'react-icons/io5';

import theme from '@styles/theme';

import { TableCell } from '@components/share/Table/table.style';

type StyledDnDListProps = {
    ACTION?: boolean;
    ARROW?: boolean;
    id: number;
    index?: number;
    moveList: (id: number, to: number) => void;
    findList: (id: number) => { list: any; index: number };
    handlerMinWidth: number;
    handlerWidth: number;
    gap?: number;
    path?: string;
    onClick?: () => void;
    children: ReactNode;
};

const StyledDnDList = ({
    ACTION = false,
    ARROW = false,
    id,
    index,
    moveList,
    findList,
    handlerMinWidth,
    handlerWidth,
    gap,
    path,
    onClick = () => null,
    children
}: StyledDnDListProps) => {
    const originalIndex = findList(id).index;
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: 'list',
            item: { id, originalIndex },
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            }),
            end: (item, monitor) => {
                const { id: droppedId, originalIndex } = item;
                const didDrop = monitor.didDrop();
                if (!didDrop) {
                    moveList(droppedId, originalIndex);
                }
            }
        }),
        [id, originalIndex, moveList]
    );

    const [, drop] = useDrop(
        () => ({
            accept: 'list',
            hover({ id: draggedId }: { id: number; originalIndex: number }) {
                if (draggedId !== id) {
                    const { index: overIndex } = findList(id);
                    moveList(draggedId, overIndex);
                }
            }
        }),
        [findList, moveList]
    );

    return (
        <DnDTableRow $isDragging={isDragging} $click={ACTION} onClick={onClick}>
            <CellFlex
                $minWidth={handlerMinWidth}
                $width={handlerWidth}
                $gap={gap}
            >
                {ARROW && (
                    <ButtonArea>
                        <ButtonUpDown
                            onClick={() =>
                                index ? moveList(id, index! - 1) : () => null
                            }
                        >
                            <IoChevronUpOutline
                                color={theme.colors.lightGrayFontColor}
                                size={16}
                            />
                        </ButtonUpDown>
                        <ButtonUpDown onClick={() => moveList(id, index! + 1)}>
                            <IoChevronDownOutline
                                color={theme.colors.lightGrayFontColor}
                                size={16}
                            />
                        </ButtonUpDown>
                    </ButtonArea>
                )}
                <Handler
                    ref={(node: any) => {
                        drag(drop(node));
                    }}
                >
                    <IoReorderThreeOutline fontSize={24} />
                </Handler>
            </CellFlex>
            {children}
        </DnDTableRow>
    );
};

export default memo(StyledDnDList);

export const DnDTableRow = styled.li<{
    $isDragging?: boolean;
    $click?: boolean;
}>`
    display: flex;
    align-items: center;
    padding: 15px 0;
    height: auto;
    min-height: 50px;
    border-bottom: ${theme.colors.ultraLightGrayBorder} 1px solid;
    opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
    transition: all.2s;

    &:hover {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }

    ${({ $click }) =>
        $click &&
        css`
            cursor: pointer;
        `}
`;
const Handler = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;
`;
const CellFlex = styled(TableCell)<{ $gap?: number }>`
    display: flex;
    align-items: center;
    gap: ${({ $gap }) => ($gap ? $gap : 10)}px;
`;
const ButtonArea = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 24px;
`;
const ButtonUpDown = styled.button`
    width: 16px;
    height: 16px;
`;
